import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { LABO } from "../data/labo";
import type { ChapterData } from "../data/content-types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CAPÍTULO 3 — "La botella sin nombre". Laboratorio: fondo, suelo y decorado propios. */
export class LaboScene extends WalkChapter {
  protected chapter: ChapterData = LABO;
  protected grid = { w: 15, h: 11 };
  protected spawn = { x: 7, y: 9 };
  protected worldSigns = [
    { id: "m_mascarilla", x: 1.4, y: 0.3 },
    { id: "p_fumar", x: 4.6, y: 0.3 },
    { id: "ghs_corrosivo", x: 8.5, y: 0.3 },
    { id: "e_lavaojos", x: 14.2, y: 5.2 },
    { id: "e_ducha", x: 14.2, y: 6.4 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("labo");
  }

  protected floorKind(x: number, y: number): P.TileKind {
    // suelo epoxi claro y limpio
    const h = (x * 928371 + y * 12347) & 255;
    return h % 17 === 0 ? "sand" : "concrete";
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0xb9c0be);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xd6dcd8, 0xd6dcd8, 0xb9c0be, 0xb9c0be, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.6 + over);
    // edificios de laboratorio limpios con cristal verde
    const sk = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    const base = H * 0.42;
    for (const [x, w, ht] of [[80, 200, 96], [340, 150, 70], [520, 220, 110], [900, 180, 84], [1120, 160, 100]] as [number, number, number][]) {
      sk.fillStyle(0xc7ccc9, 0.92); sk.fillRect(x, base - ht, w, ht);
      sk.fillStyle(0x9fc4b6, 0.5);
      for (let wy = base - ht + 12; wy < base - 8; wy += 16) for (let wx = x + 6; wx < x + w - 6; wx += 16) sk.fillRect(wx, wy, 9, 9);
    }
    // cruz verde tenue al fondo
    sk.fillStyle(0x2f8f57, 0.16); sk.fillRect(560, base - 150, 26, 90); sk.fillRect(528, base - 118, 90, 26);
    // paredes alicatadas del laboratorio
    this.buildWalls({ left: 0xd0d8d2, right: 0xe4ece6, height: 160, base: 0x9fb0a6, texture: "tile" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };
    // franja de derrame contenida (marca de suelo) + campana de extracción
    const g = this.add.graphics().setDepth(-9500);
    g.lineStyle(3, 0x2f8f57, 0.4);
    for (const yy of [5.0, 5.8]) { const a = this.iso(3, yy), b = this.iso(this.grid.w - 3, yy); g.beginPath(); g.moveTo(a.sx, a.sy + TILE_H / 2); g.lineTo(b.sx, b.sy + TILE_H / 2); g.strokePath(); }
    this.decor.push(g);
    put(P.labbench(this), 2.6, 2.8);
    put(P.fumehood(this), 12.4, 2.4); // campana de extracción
    put(P.fumehood(this), 9.6, 2.0);
    put(P.reagentShelf(this), 5.4, 1.9);
    put(P.reagentShelf(this), 13.6, 8.6);
    put(P.labsink(this), 2.4, 8.6);
    put(P.wastebin(this, 0xd0a000), 1.5, 7.4); // residuos químicos
    put(P.wastebin(this, 0xb23a2a), 2.1, 7.7); // residuos peligrosos
    put(P.barrel(this, 0x2f8f57), 2.2, 5.2); // bidón químico
    put(P.barrel(this, 0xd3651a), 2.8, 5.5);
    put(P.gascylinder(this, 0x2c6270), 1.5, 3.4);
    put(P.gascylinder(this, 0xc9a20a), 1.9, 3.7);
    put(P.gascylinder(this, 0xb23a2a), 13.4, 4.4);
    put(P.pipes(this), 12.6, 8.6);
    put(P.warningSign(this), 8.5, 4.4);
    put(P.extinguisher(this), 1.3, 6.4);
    put(P.cone(this), 6, 6.4);
    put(P.cone(this), 9, 5.2);
    put(P.fence(this), 4.6, 9.6);

    // detalle de suelo: rejilla de desagüe + salpicaduras contenidas
    const gd = this.add.graphics().setDepth(-9400);
    for (const [dx, dy] of [[6, 5.4], [11, 6]] as [number, number][]) {
      const p = this.iso(dx, dy), cy = p.sy + TILE_H / 2;
      gd.fillStyle(0x8a938e, 0.5); gd.fillRect(p.sx - 10, cy - 4, 20, 8);
      gd.lineStyle(1, 0x5a635e, 0.6); for (let i = -7; i <= 7; i += 3) { gd.beginPath(); gd.moveTo(p.sx + i, cy - 4); gd.lineTo(p.sx + i, cy + 4); gd.strokePath(); }
    }
    for (const [sx, sy2] of [[7.4, 5.3], [5.6, 5.9]] as [number, number][]) { const p = this.iso(sx, sy2); gd.fillStyle(0x2f8f57, 0.18); gd.fillEllipse(p.sx, p.sy + TILE_H / 2, 22, 10); }
    this.decor.push(gd);

    // paneles de techo fluorescentes + halo
    for (const [lx, ly] of [[3.5, 3], [7.5, 3], [11.5, 3], [5.5, 6.5], [9.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2; pg.fillStyle(0xeef4ff, 0.12); pg.fillEllipse(p.sx, pcy, 82, 39); pg.fillStyle(0xffffff, 0.12); pg.fillEllipse(p.sx, pcy, 46, 22); this.decor.push(pg); this.addPulse(pg, 0.1, 0.006, 0.9);
      const panel = P.ceilingPanel(this, 150); panel.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(panel);
    }

    // instalación en pared trasera: bandeja de servicios + botiquín (cruz verde)
    const wf = this.add.graphics().setDepth(-11900);
    const wa = this.iso(-0.5, -0.5), wb = this.iso(this.grid.w - 0.5, -0.5);
    wf.lineStyle(3, 0x9fb0a6, 0.9); wf.beginPath(); wf.moveTo(wa.sx, wa.sy - 118); wf.lineTo(wb.sx, wb.sy - 118); wf.strokePath();
    const cab = this.iso(9, -0.5);
    wf.fillStyle(0xe9f4ec, 1); wf.fillRect(cab.sx - 12, cab.sy - 108, 24, 22); wf.lineStyle(2, 0x2f8f57, 1); wf.strokeRect(cab.sx - 12, cab.sy - 108, 24, 22);
    wf.fillStyle(0x2f8f57, 1); wf.fillRect(cab.sx - 2, cab.sy - 104, 4, 14); wf.fillRect(cab.sx - 7, cab.sy - 99, 14, 4);
    this.decor.push(wf);

    // vapor ascendiendo del derrame (6,6) — el charco humea
    this.addRisers(6, 6, { count: 4, color: 0xc4e0b0, alpha: 0.32, r: 5, rise: 44, sp: 0.006, grow: 1.9, dyBase: 6, depth: 30000 });
    this.addRisers(6, 6, { count: 3, color: 0xd8ecc8, alpha: 0.24, r: 4, rise: 50, sp: 0.0045, grow: 2.2, dx: 6, dyBase: 4, depth: 30000 });

    if (this.state.chapter("labo").done) this.onResolved();
  }

  protected onResolved(): void {
    const v = this.iso(6, 3);
    this.add.text(v.sx, v.sy - 40, "extracción ON", { fontSize: "10px", color: "#1e5b38", fontStyle: "bold" }).setOrigin(0.5).setDepth(70);
  }
}
